import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { decideStatJournal, errorMessage } from "@/api/closeReview";
import type { StatJournal } from "@/api/closeReview";
import { formatValue } from "./shared";

// The month's statistical journals, calculated by fsp-bi-hub on business day 5. An approver approves (which
// posts the journal to NetSuite) or rejects (with a comment) each one; fsp-bi-hub does the posting.

const STATUS: Record<StatJournal["status"], { label: string; color: "warning" | "info" | "success" | "error" }> = {
  pending: { label: "Waiting for approval", color: "warning" },
  approved: { label: "Approved, posting", color: "info" },
  posted: { label: "Posted", color: "success" },
  rejected: { label: "Rejected", color: "error" },
};

type Choice = "approve" | "reject";

function DecisionDialog({ journal, choice, month, onClose }: { journal: StatJournal; choice: Choice; month: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [comment, setComment] = useState("");
  const decide = useMutation({
    mutationFn: () => decideStatJournal(month, journal.id, choice, comment),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["close-review"] }); onClose(); },
    onError: () => qc.invalidateQueries({ queryKey: ["close-review"] }),
  });
  const approve = choice === "approve";
  return (
    <Dialog open onClose={decide.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{approve ? "Approve and post" : "Reject"} {journal.label}?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            {approve
              ? "Approving posts this journal to NetSuite now. It can't be undone from here."
              : "Rejecting holds the journal and emails David Kimbriel your comment. Nothing is posted."}
          </Typography>
          <TextField label={approve ? "Comment (optional)" : "What's wrong? (required)"} multiline minRows={2} value={comment}
            onChange={(e) => setComment(e.target.value)} autoFocus={!approve} />
          {decide.isError && <Alert severity="error">{errorMessage(decide.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={decide.isPending}>Cancel</Button>
        <Button variant="contained" color={approve ? "success" : "error"} onClick={() => decide.mutate()}
          disabled={decide.isPending || (!approve && !comment.trim())}>
          {decide.isPending ? (approve ? "Posting..." : "Rejecting...") : approve ? "Approve and post" : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function JournalCard({ journal, month, canApprove, highlighted }: { journal: StatJournal; month: string; canApprove: boolean; highlighted: boolean }) {
  const [choice, setChoice] = useState<Choice | null>(null);
  const status = STATUS[journal.status] ?? STATUS.pending;
  const withDept = journal.lines.some((l) => l.department);
  const total = journal.lines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
  return (
    <Paper id={journal.external_id} variant="outlined"
      sx={{ p: 2, scrollMarginTop: 80, borderColor: highlighted ? "info.main" : undefined, borderWidth: highlighted ? 2 : 1 }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{journal.label}</Typography>
            <Chip size="small" color={status.color} label={status.label} />
            {journal.evidence_url && (
              <Link href={journal.evidence_url} target="_blank" rel="noopener noreferrer" underline="hover" variant="body2" sx={{ color: "info.dark" }}>
                Source records
              </Link>
            )}
            {journal.netsuite_url && (
              <Link href={journal.netsuite_url} target="_blank" rel="noopener noreferrer" underline="hover" variant="body2"
                sx={{ color: "info.dark", display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                Open in NetSuite <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Link>
            )}
          </Stack>
          {journal.status === "pending" && canApprove && (
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="contained" color="success" onClick={() => setChoice("approve")}>Approve and post</Button>
              <Button size="small" variant="outlined" color="error" onClick={() => setChoice("reject")}>Reject</Button>
            </Stack>
          )}
        </Stack>
        {journal.decided_by && (
          <Typography variant="body2" color="text.secondary">
            {journal.status === "rejected" ? "Rejected" : "Approved"} by {journal.decided_by}
            {journal.decided_at ? ` on ${dayjs(journal.decided_at).format("MMM D, h:mm A")}` : ""}
            {journal.comment ? `: ${journal.comment}` : ""}
          </Typography>
        )}
        {journal.status === "pending" && !canApprove && (
          <Typography variant="body2" color="text.secondary">Waiting for David Fisk or Karen Vazquez to approve.</Typography>
        )}
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Statistic</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Location</TableCell>
                {withDept && <TableCell>Department</TableCell>}
                <TableCell align="right">Quantity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {journal.lines.map((l, i) => (
                <TableRow key={i}>
                  <TableCell>{l.statistic}</TableCell>
                  <TableCell>{l.class}</TableCell>
                  <TableCell>{l.location}</TableCell>
                  {withDept && <TableCell>{l.department}</TableCell>}
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{formatValue(l.quantity, "number")}</TableCell>
                </TableRow>
              ))}
              {journal.lines.length > 1 && new Set(journal.lines.map((l) => l.statistic)).size === 1 && (
                <TableRow>
                  <TableCell colSpan={withDept ? 4 : 3} sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{formatValue(total, "number")}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Stack>
      {choice && <DecisionDialog journal={journal} choice={choice} month={month} onClose={() => setChoice(null)} />}
    </Paper>
  );
}

export function StatJournals({ journals, month, canApprove }: { journals: StatJournal[]; month: string; canApprove: boolean }) {
  const [hash, setHash] = useState(() => window.location.hash.slice(1));
  useEffect(() => {
    // An approval email links to #<journal>; bring that card into view once it's on the page.
    if (hash) document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    const onHash = () => setHash(window.location.hash.slice(1));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [hash]);
  const count = (status: StatJournal["status"]) => journals.filter((j) => j.status === status).length;
  const waiting = count("pending"), rejected = count("rejected"), posting = count("approved");
  const allPosted = count("posted") === journals.length;
  const parts = [
    waiting && `${waiting} waiting for approval`,
    posting && `${posting} approved and still posting`,
    rejected && `${rejected} rejected (recalculated on the next run once the source data is fixed)`,
  ].filter(Boolean);
  return (
    <Stack spacing={2}>
      <Alert severity={allPosted ? "success" : rejected ? "error" : "warning"} variant="outlined">
        {allPosted
          ? "Every statistical journal is approved and posted."
          : `${journals.length} journal${journals.length === 1 ? "" : "s"}: ${parts.join(", ")}. ` +
            "Approving posts the journal to NetSuite; the financial package is built once every journal is posted."}
      </Alert>
      {journals.map((j) => (
        <JournalCard key={j.id} journal={j} month={month} canApprove={canApprove} highlighted={j.external_id === hash} />
      ))}
    </Stack>
  );
}
